/**
 * ============================================================================
 * USE COPY TO CLIPBOARD HOOK
 * ============================================================================
 * 
 * Custom hook for copying text to clipboard
 */

import { useState, useCallback } from 'react';

/**
 * Copy to clipboard hook
 * @returns {Object} { copy, copied, error }
 */
export const useCopyToClipboard = () => {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Copy text to clipboard
   */
  const copy = useCallback(async (text) => {
    try {
      setError(null);
      setCopied(false);

      if (typeof navigator === 'undefined' || !navigator.clipboard) {
        throw new Error('Clipboard API not available');
      }

      await navigator.clipboard.writeText(text);
      setCopied(true);

      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {
      setError(err.message || 'Failed to copy to clipboard');
      setCopied(false);

      // Fallback for older browsers
      try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        textArea.style.pointerEvents = 'none';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopied(true);
        setError(null);
        setTimeout(() => {
          setCopied(false);
        }, 2000);
      } catch (fallbackErr) {
        setError(fallbackErr.message || 'Failed to copy to clipboard');
      }
    }
  }, []);

  /**
   * Reset state
   */
  const reset = useCallback(() => {
    setCopied(false);
    setError(null);
  }, []);

  return { copy, copied, error, reset };
};

export default useCopyToClipboard;
